export type RoutineChecklistEntityKind = 'trip' | 'workOrder' | 'generic';

/**
 * Infers the routine entity from the API's routine type string (for example, checklistType on a task).
 * Work order is detected before trip so values like "Pre-Work Order" are classified correctly.
 */
export function routineChecklistEntityKindFromLabel(
  routineTypeLabel: string | null | undefined
): RoutineChecklistEntityKind {
  const t = (routineTypeLabel ?? '').trim();
  if (!t) {
    return 'trip';
  }
  if (/\bwork\s*order\b/i.test(t) || /workorder/i.test(t.replace(/\s+/g, ''))) {
    return 'workOrder';
  }
  if (/\btrip\b/i.test(t)) {
    return 'trip';
  }
  return 'generic';
}

export function routineChecklistTabTitleKeys(kind: RoutineChecklistEntityKind): { pre: string; post: string } {
  switch (kind) {
    case 'workOrder':
      return {
        pre: 'compliance.shared.routineChecklist.tabTitles.workOrder.pre',
        post: 'compliance.shared.routineChecklist.tabTitles.workOrder.post'
      };
    case 'trip':
      return {
        pre: 'compliance.shared.routineChecklist.tabTitles.trip.pre',
        post: 'compliance.shared.routineChecklist.tabTitles.trip.post'
      };
    default:
      return {
        pre: 'compliance.shared.routineChecklist.tabTitles.generic.pre',
        post: 'compliance.shared.routineChecklist.tabTitles.generic.post'
      };
  }
}

/**
 * Short phase labels used in the "Routine Type" column of a routine task's expanded sub-table,
 * where one row is rendered per phase.
 */
export function routineChecklistLegLabelKeys(kind: RoutineChecklistEntityKind): { pre: string; post: string } {
  switch (kind) {
    case 'workOrder':
      return {
        pre: 'compliance.shared.routineChecklist.legLabels.workOrder.pre',
        post: 'compliance.shared.routineChecklist.legLabels.workOrder.post'
      };
    case 'trip':
      return {
        pre: 'compliance.shared.routineChecklist.legLabels.trip.pre',
        post: 'compliance.shared.routineChecklist.legLabels.trip.post'
      };
    default:
      return {
        pre: 'compliance.shared.routineChecklist.legLabels.generic.pre',
        post: 'compliance.shared.routineChecklist.legLabels.generic.post'
      };
  }
}

export function routineComplianceTasksPhaseHeaderKeys(kind: RoutineChecklistEntityKind): {
  preDate: string;
  preStatus: string;
  postDate: string;
  postStatus: string;
} {
  switch (kind) {
    case 'workOrder':
      return {
        preDate: 'compliance.shared.routineChecklist.phaseHeaders.workOrder.preDate',
        preStatus: 'compliance.shared.routineChecklist.phaseHeaders.workOrder.preStatus',
        postDate: 'compliance.shared.routineChecklist.phaseHeaders.workOrder.postDate',
        postStatus: 'compliance.shared.routineChecklist.phaseHeaders.workOrder.postStatus',
      };
    case 'trip':
      return {
        preDate: 'compliance.shared.routineChecklist.phaseHeaders.trip.preDate',
        preStatus: 'compliance.shared.routineChecklist.phaseHeaders.trip.preStatus',
        postDate: 'compliance.shared.routineChecklist.phaseHeaders.trip.postDate',
        postStatus: 'compliance.shared.routineChecklist.phaseHeaders.trip.postStatus',
      };
    default:
      return {
        preDate: 'compliance.shared.routineChecklist.phaseHeaders.generic.preDate',
        preStatus: 'compliance.shared.routineChecklist.phaseHeaders.generic.preStatus',
        postDate: 'compliance.shared.routineChecklist.phaseHeaders.generic.postDate',
        postStatus: 'compliance.shared.routineChecklist.phaseHeaders.generic.postStatus',
      };
  }
}
